import { ChangeDetectorRef, Component, Input, OnInit, forwardRef, Inject } from '@angular/core';
import { NgbActiveModal }                              from '@ng-bootstrap/ng-bootstrap';
import { NgForm }                                      from '@angular/forms';
import { TranslateService }                            from '@ngx-translate/core';

import { ApiService }          from '../api/api.service';
import { CompanyService }      from '../companies/company.service';
import { ConfirmService }      from '../messages/confirm/confirm.service';
import { CurrencyService }     from '../core/services/currency.service';
import { DomainParserService } from '../core/services/domain.parser.service';
import { FlipperSidesModel }   from '../shared/components/flipper/typings/flipper.sides';
import { MessagesService }     from '../messages/messages.service';
import { ProductService }      from './product.service';
import { SessionService }      from '../sessions/session.service';
import { Table }               from '../table/types/table';
import { VendorModel }         from '../vendors/models/vendor';
import { VendorSearchService } from '../vendors/vendor.search.service';

@Component({
  selector   : 'products-advanced-search',
  template: require('./products.advanced-search.component.html'),
})
export class ProductsAdvancedSearchComponent implements OnInit {
  @Input() groups  : any;
  @Input() product : any;
  @Input() products: Array<any>;
  @Input() types   : any;

  activeSide   : FlipperSidesModel = 'front';
  selectedItems: Array<any>        = [];

  category      : any;
  productGroup  : any;
  session       : any;
  vendor        : VendorModel;

  itemCodeValidatorOptions : any = {
    checkInitially: true
  };

  wasHeadOfficeChecked = false;

  tableSettings : Table = {
    columns: [
      {
        field : 'item_code',
        header: {
          text: this.translateService.instant('PRODUCT_SPACE.ITEM_CODE')
        }
      },
      {
        field : 'description',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.DESCRIPTION')
        }
      },
      {
        field : 'description2',
        header: {
          text: this.translateService.instant('INSPECTIONS_SPACE.DETAILS')
        }
      },
      {
        field : 'brand',
        header: {
          text: this.translateService.instant('PRODUCT_SPACE.BRAND')
        }
      },
      {
        field : 'quantity_on_hand',
        header: {
          text: this.translateService.instant('PRODUCT_SPACE.QTY')
        }
      },
      {
        pipe  : 'globalCurrency',
        field : 'retail_price',
        header: {
          text: this.translateService.instant('JS_SPACE.COLUMNS.PRICE')
        }
      },
      {
        pipe  : 'globalCurrency',
        field : 'cost',
        header: {
          text: this.translateService.instant('PRODUCT_SPACE.COST')
        }
      }
    ],
    empty: {
      text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_PRODUCTS_FOUND')
    },
    pagination: {
      enabled   : true,
      pageSize  : 5,
      pageSizes : [ 5, 10 ],
      queryCount: () => this.apiService.get('/products/product_list_count', [ this.product.item_code ])
    },
    rows: {
      highlight: {
        enabled: true
      },
      select: {
        enabled: true
      }
    },
    sorting: {
      enabled: false
    },
  };

  constructor (
    public  activeModal        : NgbActiveModal,
    @Inject(forwardRef(() => ApiService)) private apiService         : ApiService,
    private changeDetectorRef  : ChangeDetectorRef,
    @Inject(forwardRef(() => CompanyService))
    private companyService     : CompanyService,
    @Inject(forwardRef(() => ConfirmService)) private confirmService     : ConfirmService,
    @Inject(forwardRef(() => CurrencyService))
    private currencyService    : CurrencyService,
    @Inject(forwardRef(() => DomainParserService)) public  domainParserService:DomainParserService,
    @Inject(forwardRef(() => MessagesService)) private messagesService    : MessagesService,
    @Inject(forwardRef(() => ProductService)) private productService     : ProductService,
    @Inject(forwardRef(() => SessionService))
    private sessionService     : SessionService,
    private translateService   : TranslateService,
    @Inject(forwardRef(() => VendorSearchService)) private vendorSearchService: VendorSearchService
  ) {
    this.session = this.sessionService.base();
  }

  changeSide ( side : FlipperSidesModel = 'back' ) : void {
    this.activeSide = side;

    this.changeDetectorRef.detectChanges();

    if ( this.activeSide === 'back' && this.product.item_code ) {
      this.checkHeadOffice();
    }
  }

  checkHeadOffice () : void {
    if ( this.wasHeadOfficeChecked ) {
      return;
    }

    this.productService.findInHeadOffice(this.product)
    .then(( product ) => {
      if (product) {
        this.product = product;

        if ( this.product.vendor_id ) {
          this.getVendor(this.product.vendor_id);
        }

        this.wasHeadOfficeChecked = true;
      }
    })
    .finally(() => {
      this.wasHeadOfficeChecked = true;
    });
  }

  create ( form : NgForm ) : void {
    let product;
    /**
     * WOR-2733
     *
     * certain validations need to be made before a save can happen.
     * The item code needs to be checked for uniqueness and if the
     * user is linked to a head office, then the product needs to be
     * looked up in the head office. A lot of times a user will enter
     * an item code then hit save right away. This doesn't give the
     * blur effect time to complete so we check that as well as the
     * async validator flag. Keep calling save until those validations
     * are resolved.
     */
    /**
     * TODO: Item Code Duplicate Check
     */
    // if ( angular.isUndefined( form.form.item_code.duplicate ) || !this.wasHeadOfficeChecked ) {
    //   return $timeout(() => $scope.create(form));
    // }

    this.productService.save(this.product, form)
    .then(( response ) => {
      product = response;

      return this.companyService.isSubscriptionPlan('platinum') && this.companyService.isBranchOwner()
        ? this.confirmService.save(this.translateService.instant('JS_SPACE.CONFIRM.ADD_PRODUCT_TO_OTHER_BRANCHES'))
          .then(updateProducts => updateProducts !== undefined && updateProducts
            ? this.productService.pushDownPricingInfo(product)
            : Promise.resolve())
        : Promise.resolve();
    })
    .then(() => this.activeModal.close({
      selection   : product,
      isNewProduct: true
    }))
    .catch(err => {
      console.log(err);

      this.messagesService.error(err);
    });
  }

  getCategory ( id ?: string ) : void {
    if ( id ) {
      this.apiService.get('/product_category', id)
      .then(category => {
        this.product.product_category_name = category.name;

        this.category = {
          category_name    : category.name,
          id               : category.id,
          purchase_tax_rate: category.purchase_tax_rate || this.currencyService.getInfo().purchases_tax_rate
        };

        if ( this.product.cost ) {
          this.updateCostIncludingTax();
        }
      })
      .catch(err => this.messagesService.error(err));
    }
    else {
      this.product.product_category_name = '';

      this.category = {
        category_name    : '',
        id               : '',
        purchase_tax_rate: this.currencyService.getInfo().purchases_tax_rate
      };

      if ( this.product.cost ) {
        this.updateCostIncludingTax();
      }
    }
  }

  getProductGroup () : void {
    if ( this.product.product_group_name ) {
      this.apiService.get('/product_group', this.product.product_group_name, undefined, { skipEncoding: true })
      .then(group => {
        this.productGroup = group;

        if ( this.productGroup.length ) {
          this.getCategory(this.productGroup[ 0 ].product_category_id);
        }
      });
    }
    else {
      this.getCategory();
    }
  }

  getProducts ( params = { offset: 0, pageSize: 5 } ) : Promise<any> {
    return this.apiService.get('/products/product_list', [
        this.product.item_code,
        params.offset,
        params.pageSize,
      ], null, { skipEncoding: true })
      .then(products => {
        this.products = products;

        if ( !products.length && this.activeSide === 'front' ) {
          this.changeSide('back');
        }

        return products;
      });
  }

  getVendor ( id : string ) : void {
    this.vendorSearchService.get(id)
    .then(( vendor : VendorModel ) => {
      this.vendor = vendor;

      this.product.vendor_name = vendor.company_name;
    })
    .catch(err => {
      this.messagesService.error(err);
    });
  }

  handleVendorChange ( vendor : string | VendorModel ) : void {
    if ( !vendor ) {
      this.product.vendor_id   = '';
      this.product.vendor_name = '';
    }
  }

  ngOnInit () : void {
    this.getProductGroup();

    if ( !this.products.length && this.activeSide === 'front' ) {
      this.changeSide('back');
    }

    if ( this.product.vendor_id ) {
      this.getVendor(this.product.vendor_id);
    }
  }

  onRowClick ( event : any ) : void {
    this.selectedItems = this.products.filter((row) => row.selected);
  }

  onRowDoubleClick ( event : any ) : void {
    this.selectedItems = [ event.row ];

    this.select();
  }

  select () : void {
    let isNewProduct = false;

    this.product = this.selectedItems[ 0 ];

    if ( this.product.company_id != this.companyService.getId() ) {
      this.apiService.get('/products/copy_from_branch', this.product.id)
      .then(result => {
        this.product = result.product;
        isNewProduct = true;

        this.activeModal.close({
          selection: this.product,
          isNewProduct
        });
      });
    }
    else {
      this.activeModal.close({
        selection: this.product,
        isNewProduct
      });
    }
  }

  setVendor ( vendor : VendorModel ) : void {
    this.product.vendor_id   = vendor.id;
    this.product.vendor_name = vendor.company_name;
  }

  updateCostExcludingTax () : void {
    this.product.cost = this.product.gst_free
      ? this.product.cost_including_tax
      : this.productService.calculateCostWithoutTax(this.product.cost_including_tax, this.category.purchase_tax_rate);
  }

  updateCostIncludingTax () : void {
    this.product.cost_including_tax = this.product.gst_free
      ? this.product.cost
      : this.productService.calculateCostWithTax(this.product.cost, this.category.purchase_tax_rate);
  }

  validateItemCode ( item_code : string ) : Promise<any> {
    return this.productService.validateItemCode({ item_code });
  }
}
